import { Loader } from "common/Loader"
import moment from "moment"
import { Fragment, useEffect, useState } from "react"
import { MetricCard } from "pages/BlackSparrow"
import ReactApexChart from "react-apexcharts"
import DatePicker from "react-datepicker"
import UserService from "services/user.service"
import { exportToCSV } from "utils/funcs"

const service = new UserService()

const TAGS = [
  "YESTERDAY",
  "TODAY",
  "WEEKTODATE",
  "LASTWEEK",
  "LASTMONTH",
  "MONTHTODATE",
  "YEARTODATE",
  "CUSTOM",
]

export const BlitzStats = () => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [date, setDate] = useState("MONTHTODATE")
  const [showCustom, setShowCustom] = useState(false)
  const [startDate, setStartDate] = useState<any>(null)
  const [endDate, setEndDate] = useState<any>(null)
  const [maxPosition, setMaxPosition] = useState<string>("")

  const [series, setSeries] = useState<any>([])
  const [categories, setCategories] = useState<string[]>([])
  const [total, setTotal] = useState({
    totalRegistered: 0,
    totalNotRegistered: 0,
  })
  const [exporting, setExporting] = useState<boolean>(false)

  useEffect(() => {
    setLoading(true)
    service
      .getBlitzStats(date, maxPosition)
      .then((resp) => {
        // Sort once so every row's name, registered and unregistered bars belong to the same person.
        const rows = [...resp.data.data].sort(
          (a, b) =>
            b.registered_count + b.not_registered_count -
            (a.registered_count + a.not_registered_count)
        )
        setData(rows as any)
        setSeries([
          {
            name: "Registered",
            data: rows.map((item: any) => item.registered_count),
          },
          {
            name: "Un Registered",
            data: rows.map((item: any) => item.not_registered_count),
          },
        ])
        setTotal({
          totalRegistered: resp.data.total_registered_user,
          totalNotRegistered: resp.data.total_unregistered_user,
        })
        setCategories(
          rows.map((item: any) => `${item.first_name} ${item.last_name}`)
        )
      })
      .catch((error) => {
        console.log(error)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [date, maxPosition])

  const handleChangeDates = (dates) => {
    const [start, end] = dates

    setStartDate(start)
    setEndDate(end)

    if ((!start && !end) || end) {
      setDate(
        `${moment(start).format("YYYY-MM-DD")},${moment(end).format(
          "YYYY-MM-DD"
        )}`
      )
    }
  }

  if (loading) return <Loader />

  const handleExport = () => {
    setExporting(true)
    exportToCSV(data, "blitzstats")
    setExporting(false)
  }

  const chartHeight = Math.max(320, categories.length * 30 + 90)

  return (
    <Fragment>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="page-title">
            Blitz Stats
          </h1>
          <p className="page-subtitle">Registered vs. unregistered blitz users per leader</p>
        </div>

        <button
          onClick={handleExport}
          disabled={exporting}
          className="btn-secondary"
        >
          {!exporting ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.75}
              stroke="currentColor"
              className="w-4 h-4 text-ink-muted"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="icon icon-tabler icon-tabler-loader-3 animate-spin"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M3 12a9 9 0 0 0 9 9a9 9 0 0 0 9 -9a9 9 0 0 0 -9 -9" />
              <path d="M17 12a5 5 0 1 0 -5 5" />
            </svg>
          )}
          <span>{exporting ? "Exporting..." : "Export CSV"}</span>
        </button>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-4">
        <div className="flex flex-wrap gap-3 [&_input]:h-9 [&_select]:h-9">
          <div className="flex flex-col gap-1 min-w-[180px]">
            <label className="field-label mb-0">Period</label>
            <select
              className="w-full px-3 text-sm"
              value={date}
              onChange={(e) => {
                if (e.target.value !== "CUSTOM") {
                  setDate(e.target.value)
                } else {
                  setShowCustom(true)
                }
              }}
            >
              {TAGS.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </div>
          {showCustom && (
            <div className="flex flex-col gap-1 min-w-[220px]">
              <label className="field-label mb-0">Custom range</label>
              <DatePicker
                selected={startDate}
                placeholderText="Select range"
                onChange={handleChangeDates}
                startDate={startDate}
                endDate={endDate}
                isClearable
                className="w-full"
                customInput={
                  <input
                    placeholder="Select date"
                    className="w-full px-3 text-sm"
                  />
                }
                selectsRange
                selectsDisabledDaysInRange
              />
            </div>
          )}
          <div className="flex flex-col gap-1 min-w-[180px]">
            <label className="field-label mb-0">Max position</label>
            <select
              className="w-full px-3 text-sm"
              value={maxPosition}
              onChange={(e) => setMaxPosition(e.target.value)}
            >
              <option value="">All</option>
              <option value="Rep">Rep</option>
              <option value="Team Lead">Team Lead</option>
              <option value="Manager">Manager</option>
              <option value="Senior Manager">Senior Manager</option>
              <option value="Regional">Regional</option>
              <option value="Senior Regional">Senior Regional</option>
              <option value="Partner">Partner</option>
            </select>
          </div>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <MetricCard
          label="Total Registered"
          displayName="Total Registered"
          value={total.totalRegistered}
          accent="sky"
          icon="user-check"
        />
        <MetricCard
          label="Total Unregistered"
          displayName="Total Unregistered"
          value={total.totalNotRegistered}
          accent="emerald"
          icon="user-minus"
        />
        <MetricCard
          label="Registered share"
          displayName="Registered share"
          value={
            total.totalRegistered + total.totalNotRegistered > 0
              ? `${Math.round(
                  (total.totalRegistered /
                    (total.totalRegistered + total.totalNotRegistered)) *
                    100
                )}%`
              : "—"
          }
          accent="violet"
          icon="chart"
        />
      </div>

      {data.length > 0 ? (
        <div className="card p-4">
          <p className="card-title mb-1">Blitz users by leader</p>
          <p className="card-subtitle mb-3">
            {categories.length} rows · hover a bar for exact counts
          </p>
          <ReactApexChart
            options={{
              chart: {
                type: "bar",
                toolbar: { show: false },
                fontFamily: "Inter, sans-serif",
                foreColor: "#8a919c",
                parentHeightOffset: 0,
                animations: { enabled: false },
              },
              colors: ["#0ea5e9", "#34d399"],
              plotOptions: {
                bar: {
                  borderRadius: 3,
                  borderRadiusApplication: "end",
                  horizontal: true,
                  barHeight: "70%",
                },
              },
              dataLabels: { enabled: false },
              stroke: { show: true, width: 1, colors: ["#fff"] },
              grid: {
                borderColor: "#eef0f3",
                strokeDashArray: 3,
                yaxis: { lines: { show: false } },
                padding: { left: 8, right: 16 },
              },
              xaxis: {
                categories: categories,
                axisBorder: { show: false },
                axisTicks: { show: false },
                labels: { style: { fontSize: "11px" } },
              },
              yaxis: {
                labels: {
                  style: { fontSize: "12px", colors: "#4b5563" },
                  maxWidth: 180,
                },
              },
              legend: {
                position: "top",
                horizontalAlign: "left",
                fontSize: "12px",
                markers: { size: 5 },
                itemMargin: { horizontal: 12 },
              },
              tooltip: { shared: true, intersect: false, theme: "light" },
            }}
            series={series}
            type="bar"
            height={chartHeight}
          />
        </div>
      ) : (
        <div className="card flex flex-col h-72 items-center justify-center">
          <div className="w-10 h-10 rounded-full bg-surface-muted flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-5 text-ink-muted"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z"
              />
            </svg>
          </div>
          <p className="mt-3 text-sm font-medium text-ink-secondary">No data found</p>
          <p className="mt-0.5 text-xs text-ink-muted">Try a different period or position.</p>
        </div>
      )}
      {/* <Table
        data={data}
        loadingData={loading}
        columns={columns}
        totalItems={data.length}
        filters={
          <div>
           

               
          </div>
        }
        exportButton={false}
      /> */}
    </Fragment>
  )
}
